import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import styles from './DataTable.module.css';

const DataTable = ({
    columns,
    data,
    loading = false,
    pagination = null,
    onPageChange = null,
    onSort = null,
    onRowClick = null,
}) => {
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSort = (column) => {
        if (!column.sortable) return;

        const newDirection = sortField === column.accessor && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(column.accessor);
        setSortDirection(newDirection);

        if (onSort) {
            onSort(column.accessor, newDirection);
        }
    };

    const getValue = (row, accessor) => {
        if (typeof accessor === 'function') {
            return accessor(row);
        }
        return row[accessor];
    };

    const renderCell = (row, column) => {
        const value = getValue(row, column.accessor);

        // If cell is a function, pass both value and the full row
        if (column.cell) {
            return column.cell(value, row);
        }

        return value;
    };

    const renderPagination = () => {
        if (!pagination || !onPageChange) return null;

        const { page, totalPages } = pagination;

        return (
            <div className={styles.pagination}>
                <button
                    onClick={() => onPageChange(1)}
                    disabled={page === 1}
                    className={styles.pageButton}
                    title="First Page"
                >
                    <FiChevronsLeft size={16} />
                </button>
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className={styles.pageButton}
                    title="Previous Page"
                >
                    <FiChevronLeft size={16} />
                </button>
                <span className={styles.pageInfo}>
                    Page {page} of {totalPages}
                </span>
                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className={styles.pageButton}
                    title="Next Page"
                >
                    <FiChevronRight size={16} />
                </button>
                <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={page === totalPages}
                    className={styles.pageButton}
                    title="Last Page"
                >
                    <FiChevronsRight size={16} />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {columns.map((column, index) => (
                                <th
                                    key={index}
                                    style={{ width: column.width }}
                                    className={column.sortable ? styles.sortable : ''}
                                    onClick={() => handleSort(column)}
                                >
                                    {column.Header}
                                    {column.sortable && sortField === column.accessor && (
                                        <span className={styles.sortIcon}>
                                            {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className={styles.noData}>
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            data.map((row, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    onClick={() => onRowClick && onRowClick(row)}
                                    className={onRowClick ? styles.clickable : ''}
                                >
                                    {columns.map((column, colIndex) => (
                                        <td key={colIndex}>
                                            {renderCell(row, column)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {renderPagination()}
        </div>
    );
};

export default DataTable;